import React, { FC, memo, useState } from 'react';

import { Typography } from 'antd';
import { PhoneOutlined, LockOutlined } from '@ant-design/icons';

import { PhoneIdentity } from '@/types/types';
import { SharedUserToDisplay } from '../../../../../stores';
// TODO: move styles from table to this component
import styles from '../ShareSettingsTable.module.scss';
import PhoneInputEdit from '@/components/Common/PhoneInput/PhoneInputEdit';
import PasswordEdit from '@/components/Common/PasswordEdit';
import { useStores } from '@/components/hooks';

const { Text } = Typography;

interface RecipientCellProps {
    cellValue: string;
    phoneInfo: PhoneIdentity;
    setPhone: (email: string, value: PhoneIdentity) => void;
    recipientForPhoneEditing: string;
    setRecipientForPhoneEditing: (value: string) => void;
    sharedUser: SharedUserToDisplay;
    fileOwnerId: string;
    showPhone?: boolean;
    showPassword?: boolean;
}

const RecipientCell: FC<RecipientCellProps> = ({
    cellValue,
    phoneInfo,
    setPhone,
    recipientForPhoneEditing,
    setRecipientForPhoneEditing,
    sharedUser,
    fileOwnerId,
    showPhone = false,
    showPassword = false,
}) => {
    const [isPasswordEditing, setIsPasswordEditing] = useState(false);
    const [isPasswordSaving, setIsPasswordSaving] = useState(false);
    const { sharedUsersStore } = useStores();

    const isEditable = (sharedUser.userId !== fileOwnerId) && sharedUser.recipientType !== 'group';
    const hasPhoneEditIcon = isEditable && showPhone;
    const hasPasswordEditIcon = isEditable && showPassword;

    const handlePasswordSave = async (password: string): Promise<void> => {
        setIsPasswordSaving(true);
        try {
            await sharedUsersStore.setRecipientPassword(cellValue, password);
            setIsPasswordEditing(false);
        } finally {
            setIsPasswordSaving(false);
        }
    };

    if (recipientForPhoneEditing === cellValue) {
        return (
            <PhoneInputEdit
                prefix={phoneInfo?.prefix}
                phone={phoneInfo?.phone}
                onSubmit={(val) => setPhone(cellValue, val)}
                onClose={() => setRecipientForPhoneEditing('')}
            />
        );
    }

    if (isPasswordEditing) {
        return (
            <PasswordEdit
                onSave={handlePasswordSave}
                onCancel={() => setIsPasswordEditing(false)}
                isLoading={isPasswordSaving}
            />
        );
    }

    return (
        <Text
            delete={sharedUser.deleted}
            title={cellValue}
            className={styles.email}
        >
            {cellValue}
            {hasPhoneEditIcon && (
                <PhoneOutlined
                    className={styles['edit-email']}
                    onClick={() => setRecipientForPhoneEditing(cellValue)}
                />
            )}
            {hasPasswordEditIcon && (
                <LockOutlined
                    className={styles['edit-email']}
                    onClick={() => setIsPasswordEditing(true)}
                />
            )}
        </Text>
    );
};

export default memo(RecipientCell);
